import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { useGetAllCompaniesQuery } from "../toolkitServices/companyApi";
import { useAddRoleMutation } from "../toolkitServices/roleApi";
import { MODULES } from "../utils/helpers";
import {
  FaArrowLeft,
  FaBuilding,
  FaUserShield,
  FaKey,
  FaEye,
  FaPlus,
  FaEdit,
  FaTrash,
  FaCheck,
} from "react-icons/fa";
import { MdVerified } from "react-icons/md";

const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.32, ease: "easeOut" },
  },
});

const permissionMeta = {
  view: { label: "View", icon: FaEye },
  create: { label: "Create", icon: FaPlus },
  edit: { label: "Edit", icon: FaEdit },
  delete: { label: "Delete", icon: FaTrash },
  approve: { label: "Approve", icon: MdVerified },
};

const spring = {
  type: "spring",
  stiffness: 420,
  damping: 24,
};

const ToggleCheck = ({ checked, onClick }) => {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={{ scale: 0.88 }}
      whileHover={{ scale: 1.04 }}
      transition={spring}
      className={`relative inline-flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-full border transition-all duration-300 ${
        checked
          ? "border-violet-600 bg-gradient-to-br from-violet-600 to-indigo-600 shadow-[0_8px_20px_rgba(99,102,241,0.22)]"
          : "border-slate-300 bg-white hover:border-violet-300 hover:bg-violet-50"
      }`}
    >
      <AnimatePresence mode="wait">
        {checked ? (
          <motion.span
            key="on"
            initial={{ scale: 0.45, opacity: 0, rotate: -18 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            exit={{ scale: 0.45, opacity: 0, rotate: 18 }}
            transition={spring}
            className="text-white"
          >
            <FaCheck size={10} />
          </motion.span>
        ) : (
          <motion.span
            key="off"
            initial={{ scale: 0.75, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.75, opacity: 0 }}
            className="h-2.5 w-2.5 rounded-full bg-slate-300"
          />
        )}
      </AnimatePresence>
    </motion.button>
  );
};

const CreateRole = () => {
  const { data: companiesResp } = useGetAllCompaniesQuery({ page: 1, limit: 50 });
  const companies = companiesResp?.data || [];

  const [addRole, { isLoading, isSuccess, error }] = useAddRoleMutation();

  const [permissions, setPermissions] = useState(
    MODULES.map((m) => ({
      moduleKey: m.key,
      permissions: {
        view: true,
        create: true,
        edit: true,
        delete: true,
        approve: true,
      },
    }))
  );

  const togglePermission = (moduleKey, field) => {
    setPermissions((prev) =>
      prev.map((mod) =>
        mod.moduleKey === moduleKey
          ? {
              ...mod,
              permissions: {
                ...mod.permissions,
                [field]: !mod.permissions[field],
              },
            }
          : mod
      )
    );
  };

  const toggleAll = (moduleKey) => {
    setPermissions((prev) =>
      prev.map((mod) => {
        if (mod.moduleKey !== moduleKey) return mod;

        const current = mod.permissions;
        const allChecked =
          current.view &&
          current.create &&
          current.edit &&
          current.delete &&
          current.approve;

        return {
          ...mod,
          permissions: {
            view: !allChecked,
            create: !allChecked,
            edit: !allChecked,
            delete: !allChecked,
            approve: !allChecked,
          },
        };
      })
    );
  };

  const {
    register,
    handleSubmit,
    reset,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: { company: "", roleName: "", shortName: "" },
  });

  useEffect(() => {
    const id = requestAnimationFrame(() => setFocus("roleName"));
    return () => cancelAnimationFrame(id);
  }, [setFocus]);

  useEffect(() => {
    if (isSuccess) {
      toast.success("Role created successfully!");
      reset();
    }
  }, [isSuccess, reset]);

  useEffect(() => {
    if (error) {
      toast.error(error?.data?.message || "Failed to create role!");
    }
  }, [error]);

  const onSubmit = async (form) => {
    const payload = {
      company: form.company,
      roleName: form.roleName.trim(),
      shortName: form.shortName.trim().toUpperCase(),
      permissions,
    };

    try {
      await addRole(payload).unwrap();
    } catch {}
  };

  const loading = isLoading || isSubmitting;

  return (
    <div className="w-full max-w-full overflow-x-hidden  ">
      <div className="mx-auto w-full max-w-7xl min-w-0">
        {/* Compact Header */}
<div className="max-w-7xl mx-auto">
  <motion.div
    variants={fadeUp(0)}
    initial="hidden"
    animate="visible"
    className="bg-white rounded-xl shadow-sm border border-gray-200 px-2 py-2 flex items-center gap-3"
  >
    <Link
      to="/organisation/role-master-list"
      title="Back to Role List"
      className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-gray-200 hover:bg-gray-100 transition"
    >
      <FaArrowLeft className="text-[#6f5cc5] text-sm" />
    </Link>

    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h1 className="text-xl md:text-2xl font-semibold text-[#4b3d91] leading-tight">
          Create Role
        </h1>

        
      </div>

      <p className="text-xs text-gray-500">
        Manage role details and assign module permissions easily.
      </p>
    </div>
  </motion.div>
</div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <motion.div
            variants={fadeUp(0.08)}
            initial="hidden"
            animate="visible"
            className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-[320px_minmax(0,1fr)] xl:grid-cols-[340px_minmax(0,1fr)]"
          >
            {/* Left side */}
            <div className="rounded-2xl border bg-white shadow-sm overflow-hidden lg:sticky lg:top-4 lg:self-start">
              <div className="flex items-center gap-2 px-3 py-3 bg-[#9481ce] text-white">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/10 border border-white/15">
                  <FaUserShield className="text-white text-sm" />
                </span>
                <div className="min-w-0">
                  <h2 className="text-sm font-bold truncate">Role Details</h2>
                  <p className="text-[10px] text-violet-100/90 truncate">
                    Basic information
                  </p>
                </div>
              </div>

              <div className="p-3 sm:p-4 space-y-4">
                <div>
                  <label className="mb-1.5 flex items-center gap-2 text-xs font-semibold text-slate-700">
                    <FaBuilding className="text-violet-600" size={11} />
                    Company <span className="text-rose-500">*</span>
                  </label>
                  <select
                    {...register("company", { required: "Required" })}
                    className={`w-full rounded-xl border bg-white px-3 py-2.5 text-sm outline-none transition-all duration-200 ${
                      errors.company
                        ? "border-red-400 ring-4 ring-red-50"
                        : "border-slate-300 focus:border-violet-500 focus:ring-4 focus:ring-violet-100"
                    }`}
                  >
                    <option value="">Select Company</option>
                    {companies.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.companyName}
                      </option>
                    ))}
                  </select>
                  {errors.company && (
                    <p className="mt-1 text-[11px] text-red-500">{errors.company.message}</p>
                  )}
                </div>

                <div>
                  <label className="mb-1.5 flex items-center gap-2 text-xs font-semibold text-slate-700">
                    <FaUserShield className="text-violet-600" size={11} />
                    Role Name <span className="text-rose-500">*</span>
                  </label>
                  <input
                    {...register("roleName", { required: "Required" })}
                    placeholder="Enter role name"
                    className={`w-full rounded-xl border bg-white px-3 py-2.5 text-sm outline-none transition-all duration-200 ${
                      errors.roleName
                        ? "border-red-400 ring-4 ring-red-50"
                        : "border-slate-300 focus:border-violet-500 focus:ring-4 focus:ring-violet-100"
                    }`}
                  />
                  {errors.roleName && (
                    <p className="mt-1 text-[11px] text-red-500">{errors.roleName.message}</p>
                  )}
                </div>

                <div>
                  <label className="mb-1.5 flex items-center gap-2 text-xs font-semibold text-slate-700">
                    <FaKey className="text-violet-600" size={11} />
                    Short Name
                  </label>
                  <input
                    {...register("shortName")}
                    placeholder="Enter short name"
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm uppercase outline-none transition-all duration-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100"
                  />
                </div>

                <div className="rounded-xl border bg-gradient-to-b from-slate-50 to-white px-3 py-3">
                  <div className="text-[12px] font-bold text-slate-800">
                    Quick Info
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500">
                    Permissions board right side se module-wise access manage karo.
                  </div>
                </div>
              </div>
            </div>

            {/* Right side */}
            <div className="rounded-2xl border bg-white shadow-sm overflow-hidden min-w-0">
              <div className="px-3 sm:px-4 py-3 border-b bg-gradient-to-b from-slate-50 to-white">
                <div className="min-w-0">
                  <div className="text-sm font-extrabold text-slate-800 flex items-center gap-2">
                    <span>Permissions Board</span>
                    <span className="text-[11px] text-slate-500 font-semibold">
                      ({MODULES.length})
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Manage access module-wise
                  </div>
                </div>
              </div>

              <div className="p-2.5 sm:p-3">
                <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
                  <div className="overflow-x-auto">
                    <div className="h-[290px] sm:h-[340px] lg:h-[calc(100vh-310px)] lg:max-h-[500px] overflow-y-auto">
                      <table className="w-full min-w-[720px] text-[12px] border-separate border-spacing-0">
                        <thead className="sticky top-0 z-20 bg-slate-50/95 backdrop-blur">
                          <tr>
                            <th className="min-w-[220px] px-3 py-3 text-left font-semibold text-slate-700 bg-slate-50/95 border-b border-slate-200">
                              Module
                            </th>
                            <th className="px-2 py-3 text-center font-semibold text-slate-700 bg-slate-50/95 border-b border-slate-200">
                              All
                            </th>
                            {Object.entries(permissionMeta).map(([key, item]) => {
                              const Icon = item.icon;
                              return (
                                <th
                                  key={key}
                                  className="min-w-[82px] px-2 py-3 text-center font-semibold text-slate-700 bg-slate-50/95 border-b border-slate-200"
                                >
                                  <div className="flex items-center justify-center gap-1 whitespace-nowrap">
                                    <Icon size={11} className="text-violet-600" />
                                    <span>{item.label}</span>
                                  </div>
                                </th>
                              );
                            })}
                          </tr>
                        </thead>

                        <tbody>
                          {MODULES.map((mod, index) => {
                            const perm = permissions.find((p) => p.moduleKey === mod.key);

                            const allSelected =
                              perm.permissions.view &&
                              perm.permissions.create &&
                              perm.permissions.edit &&
                              perm.permissions.delete &&
                              perm.permissions.approve;

                            return (
                              <motion.tr
                                key={mod.key}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.2, delay: index * 0.02 }}
                                className="border-b border-slate-100 hover:bg-violet-50/40 transition-colors"
                              >
                                <td className="px-3 py-3 bg-white">
                                  <div className="flex items-center gap-3 min-w-0">
                                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-100 to-indigo-100 font-semibold text-violet-700 shadow-sm">
                                      {mod?.label?.charAt(0)?.toUpperCase() || "M"}
                                    </div>
                                    <div className="min-w-0">
                                      <p className="truncate font-semibold text-slate-800">
                                        {mod.label}
                                      </p>
                                      <p className="text-[10px] text-slate-400 truncate">
                                        Module access
                                      </p>
                                    </div>
                                  </div>
                                </td>

                                <td className="px-2 py-3 text-center bg-white">
                                  <ToggleCheck
                                    checked={allSelected}
                                    onClick={() => toggleAll(mod.key)}
                                  />
                                </td>

                                {["view", "create", "edit", "delete", "approve"].map((field) => (
                                  <td key={field} className="px-2 py-3 text-center bg-white">
                                    <ToggleCheck
                                      checked={perm.permissions[field]}
                                      onClick={() => togglePermission(mod.key, field)}
                                    />
                                  </td>
                                ))}
                              </motion.tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="mt-2 block lg:hidden text-[10px] text-slate-500">
                  Swipe horizontally to see all permission columns.
                </div>
              </div>
            </div>
          </motion.div>

          {/* Bottom action buttons */}
          <div className="mt-3 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Link
              to="/organisation/role-master-list"
              className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-400 hover:bg-slate-50"
            >
              <FaArrowLeft size={12} />
              Cancel
            </Link>

            <motion.button
              type="submit"
              disabled={loading}
              whileHover={{ scale: 1.01, y: -1 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(99,102,241,0.28)] transition disabled:cursor-not-allowed disabled:opacity-70"
            >
              <FaCheck size={12} />
              {loading ? "Saving..." : "Save Role"}
            </motion.button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateRole;